import {
  ApiError,
  BrowserBackend,
  CompanyOutcome,
  type DiagnosticFailure,
  ErrorCode,
  Permission,
  PortalWalkFailure,
  ReportState,
  SessionOutcome,
  StageStatus,
  WalkMode,
  WalkStage,
  type EsocialPortalWalkResponse,
} from '../../api';
import { formatInteger } from '../../format';
import { FAILURE_MESSAGES } from './esocial';

/**
 * The words the portal-walk tab uses, and the failures it has to explain.
 *
 * A lookup module beside [`esocial`](./esocial.ts), for that module's reason:
 * the API reports a walk in English for whoever integrates against it, and the
 * person reading this tab is the product manager on staging with the A1
 * certificate, who needs pt-BR. So the tab keys its wording on the stable codes
 * #451 documents -- the stage name, its status, the walk mode -- and never
 * translates the server's sentences. Those are shown underneath, untouched.
 *
 * The portal's own words are not translated either. *Relatório solicitado com
 * sucesso.*, *Processado*, *Excedido* are already pt-BR, they are the
 * government's, and the whole point of the walk is to read exactly what the
 * portal said.
 */

export const PORTAL_WALK_COPY = {
  startTitle: 'Iniciar um percurso',
  startDescription:
    'Entra no portal web do eSocial pelo gov.br com o certificado A1, como procurador da ' +
    'empresa escolhida, e percorre as telas até a Relação de Trabalhadores, uma etapa por vez. ' +
    'Cada tela é guardada — captura e HTML — antes de qualquer conclusão sobre ela.',
  company: 'Empresa',
  companyHint: 'O CNPJ da empresa é o empregador representado no perfil de procurador.',
  companyPlaceholder: 'Escolha uma empresa',
  companiesLoading: 'Carregando empresas…',
  mode: 'O que fazer',
  modeHint:
    'O portal permite 10 solicitações de relatório por dia, por usuário e empregador. Só um ' +
    'percurso de solicitação que chega à etapa de solicitar gasta uma; pare antes para ' +
    'trabalhar nas etapas anteriores sem gastar nenhuma.',
  stopAfter: 'Parar depois de',
  stopAfterHint: 'As etapas seguintes ficam registradas como não executadas.',
  stopAfterAll: 'Ir até o fim',
  timeout: 'Tempo limite por ação (segundos)',
  timeoutHint:
    'Quanto cada ação no portal pode esperar, de 5 a 300 segundos. Em branco, vale o padrão ' +
    'do ambiente (I8LIFE_BROWSER_TIMEOUT_SECONDS, 30 segundos se não configurado). Um tempo ' +
    'maior separa uma etapa lenta de uma que travou.',
  timeoutInvalid: 'Informe um número inteiro de 5 a 300, ou deixe em branco.',
  start: 'Iniciar percurso',
  starting: 'Percorrendo o portal…',
  inProgress:
    'O percurso está em andamento. Ele pode levar alguns minutos: cada etapa espera o portal ' +
    'responder e guarda o que ele mostrou. As etapas aparecem aqui quando ele terminar.',
  // A remote browser (#477): a person at it signs in to gov.br and solves the
  // captcha, and the walk waits for them. Shown instead of the two above.
  startingRemote: 'Aguardando você no navegador remoto…',
  inProgressRemote:
    'Olhe o navegador remoto: entre no gov.br e, se aparecer um captcha, resolva-o. Se o ' +
    'gov.br pedir a verificação em duas etapas, gere o código no aplicativo gov.br e digite-o ' +
    'ali. Depois disso o percurso continua sozinho, e as etapas aparecem aqui quando ele terminar.',
  noRunPermission:
    'Seu perfil não permite iniciar percursos nem consultar o histórico do portal eSocial.',

  historyTitle: 'Percursos anteriores',
  historyDescription: 'Do mais recente para o mais antigo. Abra um para ver as etapas e as telas.',
  historyCaption: 'Percursos no portal eSocial',
  historyEmpty: 'Nenhum percurso ainda',
  historyEmptyDescription: 'Quando um percurso for iniciado, ele aparece aqui.',
  historyFailed: 'Não foi possível carregar os percursos.',
  when: 'Quando',
  who: 'Quem',
  modeColumn: 'Modo',
  stopAfterColumn: 'Parar depois de',
  outcome: 'Resultado',
  stagesCompleted: 'Etapas concluídas',
  stopAfterNone: 'Até o fim',

  runTitle: 'Percurso',
  runFailed: 'Não foi possível carregar o percurso.',
  runCaption: 'Etapas do percurso no portal eSocial',
  runCompleted: 'O percurso terminou sem falhas.',
  runFailedAt: (stage: string) =>
    `O percurso parou na etapa “${stage}”. A captura e o HTML dela estão logo abaixo.`,
  bundle: 'Baixar pacote',
  bundlePending: 'Preparando pacote…',
  bundleFailed: 'Não foi possível baixar o pacote do percurso.',
  employer: 'CNPJ representado',
  environment: 'Ambiente',
  startedAt: 'Iniciado em',
  finishedAt: 'Terminado em',
  requestedBy: 'Solicitado por',
  timeoutRow: 'Tempo limite por ação',
  timeoutValue: (seconds: number) => `${formatInteger(seconds)} segundos`,
  timeoutUnknown: 'Não registrado',
  browserRow: 'Navegador',
  browserUnknown: 'Não registrado',
  captchaSolved: 'Captcha',
  captchaSolvedValue: (seconds: number | null | undefined) =>
    seconds === null || seconds === undefined
      ? 'Resolvido por uma pessoa no navegador remoto'
      : `Resolvido por uma pessoa no navegador remoto em ${formatInteger(Math.round(seconds))} segundos`,
  twoStepCompleted: 'Verificação em duas etapas',
  twoStepCompletedValue: (seconds: number | null | undefined) =>
    seconds === null || seconds === undefined
      ? 'Concluída por uma pessoa no navegador remoto'
      : `Concluída por uma pessoa no navegador remoto em ${formatInteger(Math.round(seconds))} s`,

  stage: 'Etapa',
  status: 'Situação',
  moments: 'Início e fim',
  detail: 'O que o portal mostrou',
  portalMessage: 'Mensagem do portal',
  portalState: 'Situação no portal',
  url: 'Endereço',
  pageTitle: 'Título da página',
  profilesOffered: 'Perfis oferecidos',
  reportType: 'Tipo de relatório',
  employerNumber: 'Empregador na tela',
  recordsRetrieved: 'Registros',
  downloadedFile: 'Arquivo baixado',
  failure: 'Falha',
  remedy: 'O que fazer',
  inheritedLogin:
    'Este percurso fez parte de uma sessão: o login foi o da sessão, feito uma vez para todas as ' +
    'empresas, e as telas dele ficam guardadas na sessão.',

  evidenceTitle: (stage: string) => `Telas da etapa “${stage}”`,
  screenshotAlt: (stage: string) => `Captura de tela da etapa “${stage}” no portal eSocial`,
  screenshotLoading: 'Carregando captura…',
  screenshotFailed: 'Não foi possível carregar a captura desta etapa.',
  noEvidence: 'Esta etapa não guardou nenhuma tela.',
  downloadHtml: 'Baixar HTML da página',
  downloadFile: 'Baixar arquivo',
  downloadFailed: 'Não foi possível baixar o arquivo.',

  actionsTitle: 'Capturas após cada ação',
  actionsHint:
    'A área visível da página logo depois de cada ação. Clique para ver em tamanho real.',
  actionsDropped: (count: number) =>
    `Mais ${formatInteger(count)} ${count === 1 ? 'ação' : 'ações'} sem captura ` +
    '(limite de 20 por etapa).',
  thumbnailLabel: (number: number, action: string, target: string) =>
    `Ação ${number}: ${action} — ${target}`,
  thumbnailAlt: (number: number, action: string) => `Captura após a ação ${number} (${action})`,
  thumbnailFailed: 'Captura indisponível',
  closeFull: 'Fechar captura',

  networkTitle: 'Rede',
  networkCaption: (stage: string) => `Requisições da página na etapa “${stage}”`,
  networkEmpty: 'A página não fez nenhuma requisição nesta etapa.',
  method: 'Método',
  address: 'Endereço',
  result: 'Status ou falha',
  duration: 'Duração',
  pending: 'Pendente ao fim da etapa',
  pendingSummary: (count: number) =>
    count === 1
      ? '1 requisição ainda não tinha resposta quando a etapa terminou.'
      : `${formatInteger(count)} requisições ainda não tinham resposta quando a etapa terminou.`,
  noAnswer: 'sem resposta',
  capHit: 'O rastro atingiu o limite de registros desta etapa; os excedentes não foram guardados.',
  certificateFailure: 'Certificado recusado no handshake',
  consoleTitle: 'Console',

  hopsTitle: 'Certificado apresentado pelo backend',
  hopsCaption: (stage: string) => `Passos do certificado feitos pelo backend na etapa “${stage}”`,
  hopsHint:
    'Depois de “Seu certificado digital”, o backend fez estas requisições com o certificado A1, ' +
    'sem o navegador, e entregou a sessão ao navegador no último destino. Os endereços aparecem ' +
    'sem a query string e os cookies só pelo nome.',
  hopsEmpty: 'O backend não chegou a fazer nenhuma requisição.',
  hopStatus: 'Status',
  hopWentTo: 'Destino',
  hopCookies: 'Cookies definidos',
  hopPage: 'página (sem redirecionamento)',
  hopFailed: 'falhou',
  traceLoading: 'Carregando o rastro da etapa…',
  traceFailed: 'Não foi possível carregar o rastro desta etapa.',
} as const;

/**
 * The timeout field's value: `null` when blank (the deployment's default),
 * the whole number of seconds when it is one from 5 to 300, and `undefined`
 * for anything else -- which the form refuses before asking the server.
 */
export function parseTimeout(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  if (!/^\d+$/.test(trimmed)) {
    return undefined;
  }
  const seconds = Number(trimmed);
  return seconds >= 5 && seconds <= 300 ? seconds : undefined;
}

/** The actions a screenshot follows, by the code the API names them with. */
export const WALK_ACTION_LABELS: Record<PortalWalkAction, string> = {
  navigate: 'Abrir endereço',
  click: 'Clicar',
  fill: 'Preencher',
  choose: 'Escolher',
  set_checked: 'Marcar',
};

export type PortalWalkAction = 'navigate' | 'click' | 'fill' | 'choose' | 'set_checked';

/** One request in a stage's trace. No header, body or cookie: the trace never holds one. */
export interface PortalWalkTraceRequest {
  readonly method: string;
  readonly url: string;
  readonly resource_type: string;
  readonly navigation: boolean;
  readonly started_ms: number;
  readonly ended_ms: number | null;
  readonly duration_ms: number | null;
  readonly status: number | null;
  readonly failure: string | null;
  readonly size: number | null;
}

/**
 * A stage's trace as `GET …/stages/{index}/trace` serves it (#456). The
 * contract types the artefact as bytes, so its shape is written here; the
 * backend's `trace_document` is the other half.
 */
export interface PortalWalkTrace {
  readonly format: string;
  readonly stage: WalkStage;
  readonly stage_index: number;
  readonly timeout_seconds: number | null;
  readonly ended_ms: number;
  readonly actions: readonly {
    readonly action: string;
    readonly target: string | null;
    readonly started_ms: number;
    readonly ended_ms: number | null;
    readonly duration_ms: number | null;
    readonly outcome: string | null;
  }[];
  readonly navigations: readonly {
    readonly url: string;
    readonly title: string | null;
    readonly at_ms: number;
  }[];
  readonly requests: readonly PortalWalkTraceRequest[];
  readonly pending: readonly PortalWalkTraceRequest[];
  readonly console: readonly {
    readonly level: string;
    readonly text: string;
    readonly at_ms: number;
  }[];
  readonly certificate_failure: string | null;
  readonly cap: {
    readonly per_kind: number;
    readonly hit: boolean;
    readonly dropped: Readonly<Record<string, number>>;
  };
}

/**
 * The permission #451 gates `POST /esocial/portal-walks` with, and the one its
 * reads use too. Named once so the form and the history each ask for "their"
 * permission: today they are the same, and if the run is ever split onto a
 * stronger one this is the line that changes.
 */
export const PORTAL_WALK_RUN_PERMISSION = Permission.ESOCIAL_READ;
export const PORTAL_WALK_READ_PERMISSION = Permission.ESOCIAL_READ;

/**
 * Which browser ran a walk or a diagnostic (#477), by `I8LIFE_BROWSER_BACKEND`'s
 * value. Shared with the connection page's diagnostic result.
 */
export const BROWSER_BACKEND_LABELS: Record<BrowserBackend, string> = {
  [BrowserBackend.CHROMIUM]: 'Chromium sem interface, no servidor',
  [BrowserBackend.REMOTE_CHROMIUM]: 'Chromium remoto, com uma pessoa nele',
  [BrowserBackend.MEMORY]: 'Navegador em memória (testes)',
  [BrowserBackend.DISABLED]: 'Desativado',
};

/** Every stage, by the code the API names it with. */
export const WALK_STAGE_LABELS: Record<WalkStage, string> = {
  [WalkStage.LOGIN]: 'Login no gov.br',
  [WalkStage.PROFILE]: 'Perfil de procurador',
  [WalkStage.MODULE]: 'Relatórios Gerenciais',
  [WalkStage.REQUEST]: 'Solicitar relatório',
  [WalkStage.LIST]: 'Relatórios solicitados',
  [WalkStage.DOWNLOAD]: 'Baixar relatório',
};

/**
 * The stages each mode walks, in walk order -- #451's `STAGES_OF`, which the
 * contract states in prose rather than as a schema. A collect run never
 * requests and a request run never downloads.
 */
export const WALK_STAGES_OF: Record<WalkMode, readonly WalkStage[]> = {
  [WalkMode.REQUEST]: [
    WalkStage.LOGIN,
    WalkStage.PROFILE,
    WalkStage.MODULE,
    WalkStage.REQUEST,
    WalkStage.LIST,
  ],
  [WalkMode.COLLECT]: [
    WalkStage.LOGIN,
    WalkStage.PROFILE,
    WalkStage.MODULE,
    WalkStage.LIST,
    WalkStage.DOWNLOAD,
  ],
};

export const WALK_MODE_LABELS: Record<WalkMode, string> = {
  [WalkMode.REQUEST]: 'Solicitar um novo relatório',
  [WalkMode.COLLECT]: 'Coletar um relatório já processado',
};

/** Short forms, for the history table. */
export const WALK_MODE_SHORT: Record<WalkMode, string> = {
  [WalkMode.REQUEST]: 'Solicitar',
  [WalkMode.COLLECT]: 'Coletar',
};

export const STAGE_STATUS_LABELS: Record<StageStatus, string> = {
  [StageStatus.COMPLETED]: 'Concluída',
  [StageStatus.FAILED]: 'Falhou',
  [StageStatus.SKIPPED]: 'Não executada',
  [StageStatus.INHERITED]: 'Login da sessão',
};

/**
 * `skipped` is neutral: a stage after the one that failed, or after
 * `stop_after`, is a consequence of something already on screen -- the
 * diagnostic's rule, for the diagnostic's reason.
 */
export const STAGE_STATUS_TONES: Record<StageStatus, 'ok' | 'blocking' | 'muted'> = {
  [StageStatus.COMPLETED]: 'ok',
  [StageStatus.FAILED]: 'blocking',
  [StageStatus.SKIPPED]: 'muted',
  [StageStatus.INHERITED]: 'muted',
};

/**
 * A stage's `failure_code`, in pt-BR: the diagnostic's browser and network
 * codes, and the portal's own answers a walk names (#479).
 */
export const STAGE_FAILURE_MESSAGES: Record<DiagnosticFailure | PortalWalkFailure, string> = {
  ...FAILURE_MESSAGES,
  [PortalWalkFailure.PROCURACAO_MISSING]:
    'O portal recusou o CNPJ no perfil de procurador: a empresa não deu à plataforma uma ' +
    'procuração com acesso ao eSocial Web. Ela precisa outorgar essa procuração ao CNPJ do ' +
    'certificado A1 da plataforma.',
  [PortalWalkFailure.SESSION_EXPIRED]:
    'O login da sessão tinha expirado quando esta empresa começou: o navegador voltou à tela de ' +
    'login. A sessão não entra de novo sozinha; inicie uma nova sessão para as empresas restantes.',
};

export const WALK_OUTCOME_LABELS: Record<EsocialPortalWalkResponse['outcome'], string> = {
  completed: 'Concluído',
  failed: 'Falhou',
};

export const WALK_OUTCOME_TONES: Record<EsocialPortalWalkResponse['outcome'], 'ok' | 'blocking'> = {
  completed: 'ok',
  failed: 'blocking',
};

/** Why a stage did not run, keyed on the API's `skipped_reason`. */
export const SKIPPED_REASONS: Record<'stop_after' | 'earlier_stage_failed', string> = {
  stop_after: 'O percurso foi configurado para parar antes desta etapa.',
  earlier_stage_failed: 'Uma etapa anterior falhou, então esta não foi executada.',
};

/** Our reading of the portal's state word. The word itself is always shown beside it. */
export const REPORT_STATE_LABELS: Record<ReportState, string> = {
  [ReportState.PENDING]: 'pendente',
  [ReportState.PROCESSED]: 'processado',
  [ReportState.EMPTY]: 'vazio',
  [ReportState.DONE]: 'concluído',
  [ReportState.EXCEEDED]: 'excedido',
  [ReportState.UNKNOWN]: 'palavra desconhecida',
};

/**
 * Why a walk could not *start*, as opposed to why a stage failed.
 *
 * Every refusal #451 makes before a browser is launched is a different thing to
 * go and do: turn the browser on, install a certificate, record the company's
 * CNPJ, pick a stage the mode has. A walk that ran and failed at a stage is an
 * answer, rendered as one, and never reaches this function.
 *
 * Returns `undefined` to let the shared wording stand.
 */
export function walkStartFailureMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) {
    return undefined;
  }
  if (error.code === ErrorCode.SERVICE_UNAVAILABLE) {
    return (
      'Este ambiente não tem navegador configurado, então não há como abrir o portal do ' +
      'eSocial. O navegador é ligado de propósito em cada ambiente: configure ' +
      'I8LIFE_BROWSER_BACKEND como chromium.'
    );
  }
  const fields = new Set(error.details.map((detail) => detail.field?.split('.').pop()));
  if (fields.has('certificate')) {
    return (
      'Não há certificado A1 utilizável instalado. O login no gov.br é feito com ele: instale ' +
      'ou renove o certificado na tela Certificado digital e tente de novo.'
    );
  }
  if (fields.has('company_id')) {
    return (
      'A empresa escolhida não tem CNPJ cadastrado. O perfil de procurador representa um ' +
      'empregador pelo CNPJ: cadastre o CNPJ da empresa e tente de novo.'
    );
  }
  if (fields.has('timeout_seconds')) {
    return 'O tempo limite por ação deve ficar entre 5 e 300 segundos.';
  }
  if (fields.has('stop_after')) {
    return 'A etapa escolhida para parar não faz parte deste modo. Escolha outra.';
  }
  if (error.isNotFound) {
    return 'A empresa escolhida não existe mais. Atualize a página e escolha outra.';
  }
  if (error.code === ErrorCode.UNPROCESSABLE_ENTITY) {
    return (
      'O certificado A1 instalado não pôde ser preparado para o login. Instale-o novamente a ' +
      'partir do arquivo emitido pela autoridade certificadora.'
    );
  }
  return undefined;
}

// --- Session walks (#479) ------------------------------------------------------

/**
 * Starting a session needs `esocial:write`, a stronger permission than a single
 * walk's: one session walks many companies on one login.
 */
export const PORTAL_SESSION_RUN_PERMISSION = Permission.ESOCIAL_WRITE;

export const PORTAL_SESSION_COPY = {
  title: 'Sessão',
  description:
    'Um único login no gov.br e, em seguida, cada empresa escolhida, na ordem, pelo perfil de ' +
    'Procurador de Pessoa Jurídica com o CNPJ dela. Uma empresa recusada não impede a seguinte.',
  search: 'Buscar empresa',
  searchHint: 'Digite parte do nome e adicione as empresas na ordem em que devem ser percorridas.',
  searchEmpty: 'Nenhuma empresa encontrada com esse nome.',
  add: (name: string) => `Adicionar ${name}`,
  addShort: 'Adicionar',
  chosen: 'Empresas da sessão, na ordem',
  chosenEmpty: 'Nenhuma empresa adicionada ainda.',
  chosenMissing: 'Adicione ao menos uma empresa.',
  chosenTooMany: 'Uma sessão percorre no máximo 50 empresas.',
  remove: (name: string) => `Remover ${name}`,
  removeShort: 'Remover',
  up: (name: string) => `Subir ${name}`,
  down: (name: string) => `Descer ${name}`,
  noCnpj: 'sem CNPJ',
  stopAfterLogin: 'Login no gov.br (nenhuma empresa é percorrida)',
  stopAfterHint:
    'Parar depois do login não percorre nenhuma empresa; qualquer outra etapa vale para todas.',
  start: 'Iniciar sessão',
  starting: 'Percorrendo as empresas…',
  inProgress:
    'A sessão está em andamento: primeiro o login, depois cada empresa, uma de cada vez. O ' +
    'resultado de cada empresa aparece aqui quando a sessão terminar.',
  startingRemote: 'Aguardando você no navegador remoto…',
  inProgressRemote:
    'Olhe o navegador remoto: entre no gov.br e, se aparecer um captcha, resolva-o. Se o ' +
    'gov.br pedir a verificação em duas etapas, gere o código no aplicativo gov.br e digite-o ' +
    'ali. Depois disso a sessão percorre as empresas sozinha.',
  resultTitle: 'Resultado da sessão',
  resultFailed: 'Não foi possível carregar a sessão.',
  login: 'Login compartilhado',
  duration: 'Duração total',
  durationValue: (seconds: number) => `${formatInteger(Math.round(seconds))} segundos`,
  resultCaption: 'Empresas da sessão no portal eSocial',
  position: 'Ordem',
  company: 'Empresa',
  outcome: 'Resultado',
  detail: 'Detalhe',
  companyDuration: 'Duração',
  walk: 'Percurso',
  openWalk: 'Abrir percurso',
  noWalk: 'Não percorrida',
} as const;

export const SESSION_OUTCOME_LABELS: Record<SessionOutcome, string> = {
  [SessionOutcome.COMPLETED]: 'Concluída',
  [SessionOutcome.PARTIAL]: 'Parcial',
  [SessionOutcome.FAILED]: 'Falhou',
};

export const SESSION_OUTCOME_TONES: Record<SessionOutcome, 'ok' | 'blocking' | 'muted'> = {
  [SessionOutcome.COMPLETED]: 'ok',
  [SessionOutcome.PARTIAL]: 'blocking',
  [SessionOutcome.FAILED]: 'blocking',
};

export const COMPANY_OUTCOME_LABELS: Record<CompanyOutcome, string> = {
  [CompanyOutcome.COMPLETED]: 'Concluída',
  [CompanyOutcome.FAILED]: 'Falhou',
  [CompanyOutcome.SKIPPED]: 'Não percorrida',
};

export const COMPANY_OUTCOME_TONES: Record<CompanyOutcome, 'ok' | 'blocking' | 'muted'> = {
  [CompanyOutcome.COMPLETED]: 'ok',
  [CompanyOutcome.FAILED]: 'blocking',
  [CompanyOutcome.SKIPPED]: 'muted',
};

/** Why a company of a session was not walked, keyed on its `skipped_reason`. */
export const SESSION_SKIPPED_REASONS: Record<
  'login_failed' | 'stop_after' | 'browser_closed',
  string
> = {
  login_failed: 'O login compartilhado falhou, então nenhuma empresa foi percorrida.',
  stop_after: 'A sessão foi configurada para parar depois do login.',
  browser_closed: 'O navegador foi fechado antes de a sessão chegar a esta empresa.',
};

/**
 * Why a session could not *start*: the single walk's reasons, plus a company
 * the session cannot walk (`field: company_ids`).
 */
export function sessionStartFailureMessage(error: unknown): string | undefined {
  if (
    error instanceof ApiError &&
    error.details.some((detail) => detail.field?.split('.').pop() === 'company_ids')
  ) {
    return (
      'Uma das empresas escolhidas não pode ser percorrida: ela não tem CNPJ cadastrado, não está ' +
      'visível para você ou foi escolhida duas vezes. ' +
      (error.details.find((detail) => detail.field?.endsWith('company_ids'))?.message ?? '')
    ).trim();
  }
  return walkStartFailureMessage(error);
}
